import { useCallback, useEffect, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import {
  fetchMyOrganizationTrustStatus,
  isTrustAttentionStatus,
  type OrgTrustStatus,
} from '../../../settings/infra/orgTrustStatusService';

/**
 * Loads merchant trust status for shell banner + Profile.
 * Refetches on session change, AppState active (vuelve a la app), and refresh().
 * El corte online es server-side inmediato; este hook solo actualiza el aviso en UI.
 */
export function useOrgTrustStatus() {
  const userId = useAuthStore((s) => s.session?.user?.id ?? null);
  const [status, setStatus] = useState<OrgTrustStatus | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setStatus(null);
      return;
    }
    setLoading(true);
    try {
      const next = await fetchMyOrganizationTrustStatus();
      setStatus(next);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onChange = (next: AppStateStatus) => {
      if (next === 'active') void refresh();
    };
    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [refresh]);

  return {
    status,
    loading,
    refresh,
    needsAttention: status ? isTrustAttentionStatus(status.status) : false,
  };
}
